import {
  colorLegacy,
  colorTokens,
  grantedBrandColors,
  lightColors,
  postfixObjKeys,
} from "./legacy/token-colors";

// Re-export legacy for backward compatibility
export { colorTokens, grantedBrandColors, lightColors, postfixObjKeys };

export const colors = {
  // Utility
  transparent: "transparent",

  // System - Surfaces
  backgroundPrimary: "#FAF9F8",
  backgroundSecondary: "#F5F4F1",
  white: "#FFFFFF",

  // System - Text
  primary: "#010222", // Main text color
  secondary: "#7E7C79", // Supporting text
  disabled: "#98938B", // Disabled text

  // System - UI Elements
  stroke: "#EBE7E0", // Borders, dividers
  iconFill: "#D5CFC9", // Icon fills

  navy: "#002160",
  navyDark: "#0E173B",

  positiveDark: "#4E7C23",
  positive: "#DEF5AD",
  positiveLight: "#EFFAD6",

  negativeDark: "#B84A4A",
  negative: "#EB6B6B",
  negativeLight: "#FFEEED",

  warningDark: "#B55007",
  warning: "#FCA92C",
  warningLight: "#FFEFDE",
} as const;

// V2 Design System theme tokens
export const themeTokens = {
  transparent: colors.transparent,

  surfacePrimary: colors.backgroundPrimary,
  surfaceSecondary: colors.backgroundSecondary,
  surfaceWhite: colors.white,

  textPrimary: colors.primary,
  textSecondary: colors.secondary,
  textDisabled: colors.disabled,

  uiStroke: colors.stroke,
  uiIconFill: colors.iconFill,

  brandNavy: colors.navy,
  brandNavyDark: colors.navyDark,

  alertPositive: colors.positive,
  alertPositiveDark: colors.positiveDark,
  alertPositiveLight: colors.positiveLight,

  alertNegative: colors.negative,
  alertNegativeDark: colors.negativeDark,
  alertNegativeLight: colors.negativeLight,

  alertWarning: colors.warning,
  alertWarningDark: colors.warningDark,
  alertWarningLight: colors.warningLight,
} as const;

// New UI color system primitives
export const uiPrimitives = {
  white: "#FFFFFF",
  neutrals50: "#FCFBF8",
  neutrals100: "#F3F1EC",
  neutrals200: "#EAE7E1",
  neutrals300: "#CBC7C0",
  neutrals400: "#BBB6AE",
  neutrals500: "#98938B",
  neutrals600: "#7A766F",
  neutrals700: "#5A5752",
  neutrals800: "#3D3B38",
  neutrals900: "#2A2926",
  neutrals950: "#21201E",
  amber50: "#FFEFDE",
  amber100: "#FFE2C4",
  amber200: "#FFCF9E",
  amber300: "#FFB96A",
  amber400: "#FCA92C",
  amber500: "#F08C0A",
  amber600: "#C96200",
  amber700: "#A84A00",
  amber800: "#8F3C06",
  amber900: "#6B2B05",
  amber950: "#3D1702",
  red50: "#FFF0EF",
  red100: "#FFD9D9",
  red200: "#FFBDBD",
  red300: "#F98B8B",
  red400: "#F05252",
  red500: "#E03030",
  red600: "#C42020",
  red700: "#A81A1A",
  red800: "#881414",
  red900: "#651010",
  red950: "#3A0C0C",
  green50: "#F5FDE8",
  green100: "#EFFAD6",
  green200: "#DAF997",
  green300: "#C3F555",
  green400: "#8EE23F",
  green500: "#72C828",
  green600: "#54A714",
  green700: "#4E7C23",
  green800: "#3A5E1A",
  green900: "#284213",
  green950: "#1D2F0F",
  blue50: "#E6F6FF",
  blue100: "#DFF4FF",
  blue200: "#99D9FF",
  blue300: "#66C3F5",
  blue400: "#38AAEC",
  blue500: "#1490D4",
  blue600: "#0E74B0",
  blue700: "#0B5C8E",
  blue800: "#0A476E",
  blue900: "#093654",
  blue950: "#051E30",
} as const;

// Internal-only foreground colors for text and icon to derive from for consistency. Do not consume these in app code.
const uiFgInternal = {
  fgDefault: uiPrimitives.neutrals900,
  fgSubtle: uiPrimitives.neutrals600,
  fgMuted: uiPrimitives.neutrals400,
  fgInverse: uiPrimitives.white,
  // The foreground ramp on `surfaceInverse`. Green-tinted rather than neutral
  // because our dark surface is green900, not a dark grey. `fgInverseMuted` has
  // no palette equivalent and was picked by eye; ENG-8183 gets it designed.
  fgInverseBrand: uiPrimitives.green200,
  fgInverseSubtle: uiPrimitives.green100,
  fgInverseMuted: "#BCD39A",
  fgPositiveStrong: uiPrimitives.green900,
  fgPositive: uiPrimitives.green700,
  fgNegativeStrong: uiPrimitives.red900,
  fgNegative: uiPrimitives.red600,
  fgWarningStrong: uiPrimitives.amber900,
  fgWarning: uiPrimitives.amber600,
  fgAccentStrong: uiPrimitives.blue900,
  fgAccent: uiPrimitives.blue600,
  fgBrandStrong: uiPrimitives.green800,
  fgBrand: uiPrimitives.green600,
  fgOnBrand: uiPrimitives.green900,
} as const;

export const uiTextTokens = {
  textDefault: uiFgInternal.fgDefault,
  textSubtle: uiFgInternal.fgSubtle,
  textMuted: uiFgInternal.fgMuted,
  textInverse: uiFgInternal.fgInverse,
  textInverseBrand: uiFgInternal.fgInverseBrand,
  textInverseSubtle: uiFgInternal.fgInverseSubtle,
  textInverseMuted: uiFgInternal.fgInverseMuted,
  textBrand: uiFgInternal.fgBrand,
  textBrandStrong: uiFgInternal.fgBrandStrong,
  textOnBrand: uiFgInternal.fgOnBrand,
  textAccent: uiFgInternal.fgAccent,
  textAccentStrong: uiFgInternal.fgAccentStrong,
  textPositive: uiFgInternal.fgPositive,
  textPositiveStrong: uiFgInternal.fgPositiveStrong,
  textNegative: uiFgInternal.fgNegative,
  textNegativeStrong: uiFgInternal.fgNegativeStrong,
  textWarning: uiFgInternal.fgWarning,
  textWarningStrong: uiFgInternal.fgWarningStrong,
} as const;

export const uiIconTokens = {
  iconDefault: uiFgInternal.fgDefault,
  iconSubtle: uiFgInternal.fgSubtle,
  iconMuted: uiFgInternal.fgMuted,
  iconInverse: uiFgInternal.fgInverse,
  iconInverseSubtle: uiFgInternal.fgInverseSubtle,
  iconBrand: uiFgInternal.fgBrand,
  iconBrandStrong: uiFgInternal.fgBrandStrong,
  iconOnBrand: uiFgInternal.fgOnBrand,
  iconAccent: uiFgInternal.fgAccent,
  iconAccentStrong: uiFgInternal.fgAccentStrong,
  iconPositive: uiFgInternal.fgPositive,
  iconPositiveStrong: uiFgInternal.fgPositiveStrong,
  iconNegative: uiFgInternal.fgNegative,
  iconNegativeStrong: uiFgInternal.fgNegativeStrong,
  iconWarning: uiFgInternal.fgWarning,
  iconWarningStrong: uiFgInternal.fgWarningStrong,
} as const;

export const uiBorderTokens = {
  borderDefault: uiPrimitives.neutrals300,
  borderDefaultHover: uiPrimitives.neutrals400,
  borderSubtle: uiPrimitives.neutrals100,
  borderSubtleHover: uiPrimitives.neutrals200,
  borderStrong: uiPrimitives.neutrals500,
  borderStrongHover: uiPrimitives.neutrals600,
  borderIntense: uiPrimitives.neutrals800,
  borderIntenseHover: uiPrimitives.neutrals900,
  borderInverse: uiPrimitives.white,

  borderBrand: uiPrimitives.green300,
  borderBrandHover: uiPrimitives.green400,
  borderBrandStrong: uiPrimitives.green500,
  borderBrandStrongHover: uiPrimitives.green600,
  borderAccent: uiPrimitives.blue300,
  borderAccentHover: uiPrimitives.blue400,
  borderAccentStrong: uiPrimitives.blue700,
  borderAccentStrongHover: uiPrimitives.blue800,
  borderPositive: uiPrimitives.green400,
  borderPositiveHover: uiPrimitives.green500,
  borderPositiveStrong: uiPrimitives.green700,
  borderPositiveStrongHover: uiPrimitives.green800,
  borderNegative: uiPrimitives.red300,
  borderNegativeHover: uiPrimitives.red400,
  borderNegativeStrong: uiPrimitives.red700,
  borderNegativeStrongHover: uiPrimitives.red800,
  borderWarning: uiPrimitives.amber300,
  borderWarningHover: uiPrimitives.amber400,
  borderWarningStrong: uiPrimitives.amber700,
  borderWarningStrongHover: uiPrimitives.amber800,
} as const;

export const uiBgTokens = {
  bgBrand: uiPrimitives.green100,
  bgBrandHover: uiPrimitives.green200,
  bgBrandStrong: uiPrimitives.green200,
  bgBrandStrongHover: uiPrimitives.green300,
  bgAccent: uiPrimitives.blue50,
  bgAccentHover: uiPrimitives.blue100,
  bgAccentStrong: uiPrimitives.blue400,
  bgAccentStrongHover: uiPrimitives.blue500,
  bgPositive: uiPrimitives.green100,
  bgPositiveHover: uiPrimitives.green200,
  bgPositiveStrong: uiPrimitives.green400,
  bgPositiveStrongHover: uiPrimitives.green500,
  bgNegative: uiPrimitives.red50,
  bgNegativeHover: uiPrimitives.red100,
  bgNegativeStrong: uiPrimitives.red400,
  bgNegativeStrongHover: uiPrimitives.red500,
  bgWarning: uiPrimitives.amber50,
  bgWarningHover: uiPrimitives.amber100,
  bgWarningStrong: uiPrimitives.amber400,
  bgWarningStrongHover: uiPrimitives.amber500,
  // Global surface aliases (non-contextual)
  surface0: uiPrimitives.neutrals100,
  surface0Hover: uiPrimitives.neutrals200,
  surface1: uiPrimitives.neutrals50,
  surface1Hover: uiPrimitives.neutrals100,
  surface2: uiPrimitives.white,
  surface2Hover: uiPrimitives.neutrals100,
  // The brand dark surface — the inverse counterpart to surface0/1/2, and what
  // `$textInverse*` is contrast-checked against. `surfaceStrong` is the older
  // neutral dark used by chrome (tooltips, pips); this one is the page.
  surfaceInverse: uiPrimitives.green900,
  surfaceStrong: uiPrimitives.neutrals800,
  surfaceStrongHover: uiPrimitives.neutrals950,
  // Global disabled aliases (non-contextual)
  bgDisabled: uiPrimitives.neutrals300,
  bgDisabledSubtle: uiPrimitives.neutrals200,
} as const;

export const uiUtilityTokens = {
  utilityStar: uiPrimitives.amber400,
  utilityGrellow: "#F5FF97",
  utilityOverlayDark: "rgba(75, 68, 43, 0.5)",
  utilityOverlayMedium: "rgba(75, 68, 43, 0.10)",
  utilityOverlayLight: "rgba(75, 68, 43, 0.05)",
  // The counterpart to the three above for dark surfaces: they all darken, which
  // is invisible on `surfaceInverse`, so a lift is the only overlay that reads.
  utilityOverlayInverse: "rgba(255, 255, 255, 0.12)",
} as const;

// The entire UI color API. Flat, explicit semantic tokens — stable across all
// contexts, with no remap layer or subtheme indirection.
export const uiTokens = {
  ...uiTextTokens,
  ...uiIconTokens,
  ...uiBorderTokens,
  ...uiBgTokens,
  ...uiUtilityTokens,
} as const;

// Combined export used by tamagui.config.ts
// Includes v2 tokens so they're registered as CSS variables and known to TypeScript
// The legacy theme doesn't use the theme tokens; they're only used via <Theme name="v2">
export const color = {
  ...colorLegacy,
  ...themeTokens,
  ...uiTokens,
};
